import React, { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Plane,
  Users,
  ShieldCheck,
  ArrowRight,
  Send,
  Calendar,
  Star,
  IdCard
} from 'lucide-react';

// Professional, location-accurate imagery (license-free Unsplash CDN)
const galleryImg020 = 'https://images.unsplash.com/photo-1729606684010-ed7627949061?auto=format&fit=crop&w=1600&q=80';
const galleryImg0225 = 'https://images.unsplash.com/photo-1717541262982-731503ac4dc2?auto=format&fit=crop&w=1600&q=80'; // Caribbean — cruise ship docked at a tropical beach
const galleryImg01 = 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=1600&q=80'; // Maldives — overwater villas at sunset
// const galleryImg020 = 'https://images.unsplash.com/photo-1553829176-61484f865ac3?auto=format&fit=crop&w=1600&q=80'; // Seychelles — granite boulders of Anse Source d'Argent, La Digue
const galleryImg02 = 'https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80';
const galleryImg03 = 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=1600&q=80'; // Dubai — Burj Khalifa skyline
const galleryImg04 = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1600&q=80'; // Switzerland — snow-capped Alps
const galleryImg05 = 'https://images.unsplash.com/photo-1549144511-f099e773c147?auto=format&fit=crop&w=1600&q=80'; // Paris — Eiffel Tower illuminated at night
// const galleryImg06 = 'https://images.unsplash.com/photo-1521088181836-76ba5ba991b1?auto=format&fit=crop&w=1600&q=80'; // Tokyo — aerial view of Shibuya Crossing
const galleryImg07 = 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?auto=format&fit=crop&w=1600&q=80'; // Santorini — Oia over the Aegean Sea
const GALLERY_IMAGES = [
  { src: galleryImg01, alt: 'Overwater villa at sunset in the Maldives' },
  { src: galleryImg02, alt: 'London skyline with Big Ben and the Thames' },
  { src: galleryImg03, alt: 'Burj Khalifa in Dubai' },
  { src: galleryImg04, alt: 'Snow-capped Swiss Alps' },
  { src: galleryImg05, alt: 'Eiffel Tower at night in Paris' },
  // { src: galleryImg06, alt: 'Tokyo Shibuya Crossing at dusk' },
  { src: galleryImg07, alt: 'Santorini white-washed buildings overlooking the Aegean Sea' },
  {
    src: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1600&q=80',
    alt: 'Beachside travel destination at golden hour'
  }
];

const SERVICES = [
  {
    icon: <Plane size={22} />,
    title: 'Tour packaging',
    desc: 'End-to-end planning for solo, family, and group trips. From flight comparisons to hotel reservations, we design itineraries around your budget and schedule.'
  },
  {
    icon: <IdCard size={22} />,
    title: 'Visa Support',
    desc: 'Document review, travel insurance recommendations, and airport transfer coordination. We help ensure your paperwork is accurate and streamline your travel process.'
  },
  {
    icon: <Users size={22} />,
    title: 'Group & Family Travel',
    desc: 'Coordinated flights, accommodation, and ground logistics for families, teams, and groups. We handle the complexities of group travel so you can focus on the journey.'
  },
  {
    icon: <ShieldCheck size={22} />,
    title: 'Support',
    desc: 'Assistance with schedule changes, travel documentation, and arrival logistics. We help you navigate transitions and minimize disruptions throughout your trip.'
  }
];

const FEATURED_TRIPS = [
  {
    title: 'Conferences, Retreats & Tours',
    // title: 'Cruise Tours',
    route: 'Lagos → Dubai',
    days: 'Dubai',
    inclusions: 'Conferences, corporate retreats, desert safaris, and city tours-planned and coordinated to suit your group and travel needs. ',
    // inclusions: 'Explore cruises across the Mediterranean, Caribbean, with itineraries suited to your travel style and budget',
    image: galleryImg03,
    alt: 'Burj Khalifa in Dubai'
  },
  {
    title: 'Romantic Honeymoon',
    route: 'Nigeria → United Kingdom',
    days: 'Seychelles',
    inclusions: 'Romantic getaways in captivating destinations, thoughtfully planned around your preferences and budget.',
    image: galleryImg020,
    alt: 'London skyline'
  },
  {
    title: 'Cruise Tours',
    route: 'Lagos → Maldives',
    days: 'Caribbean',
    image: galleryImg0225,
    inclusions: 'Explore cruises across the Mediterranean, Caribbean, with itineraries suited to your travel style and budget', 
    alt: 'Overwater villa in the Maldives'
  }
];

const APPROACH = [
  {
    title: 'We Listen First',
    desc: 'We start with your purpose, budget, timing, and constraints - not a generic package.'
  },
  {
    title: 'Thoughtful Planning',
    desc: 'Every detail considered, with a clear itinerary and complete cost clarity before you commit'
  },
  {
    title: 'Dedicated Support',
    desc: 'A trusted travel partner by your side from preparations to every step of your journey' ,
  }
];

const FAQS = [
  {
    q: 'How do you help with visa applications?',
    a: 'We provide document checklists, review your application for completeness, and help track deadlines. '
  },
  {
    q: 'Can you plan domestic travel within Nigeria?',
    a: 'Yes. We can arrange domestic flights, accommodation, and ground transport for routes such as Lagos to Abuja and Lagos to Port Harcourt.'
  },
  {
    q: 'Do you help with travel insurance?',
    a: 'We can recommend suitable travel insurance options for medical cover, trip delays, and luggage protection. Coverage depends on the selected policy.'
  },
  {
    q: 'Do you support students travelling abroad?',
    a: 'Yes. We help with visa document preparation, accommodation coordination, airport pickup, and arrival orientation where available.'
  }
];

const globalStyles = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Sora:wght@600;700&display=swap');

.home-root {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: var(--bg);
  color: var(--text);
  min-height: 100vh;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

.home-root * {
  box-sizing: border-box;
}

.home-root h1,
.home-root h2,
.home-root h3,
.home-root h4,
.home-root cite {
  font-family: 'Sora', 'Inter', sans-serif;
  color: var(--text);
}

.home-root p {
  color: var(--text-muted);
}

.home-root :focus-visible {
  outline: 3px solid var(--focus);
  outline-offset: 3px;
  border-radius: 8px;
}

.container {
  width: min(1180px, calc(100% - 2.5rem));
  margin-inline: auto;
}

.container.narrow {
  max-width: 880px;
}

.section {
  padding-block: clamp(4rem, 8vw, 6.75rem);
}
.sectiona {
  margin-top: 4rem;
}

.h1 {
  font-size: clamp(2.9rem, 6vw, 4.5rem);
  line-height: 1.02;
  letter-spacing: -0.04em;
  margin: 0;
  font-weight: 700;
}

.hero-title {
  margin: 1.15rem 0 1.1rem;
  max-width: 12ch;
}

.h2 {
  font-size: clamp(2rem, 4vw, 2.85rem);
  line-height: 1.08;
  letter-spacing: -0.03em;
  margin: 0 0 0.8rem;
  font-weight: 700;
}

.h3 {
  font-size: 1.14rem;
  line-height: 1.35;
  letter-spacing: -0.01em;
  margin: 0;
}

.center {
  text-align: center;
}

.section-head {
  max-width: 780px;
  margin: 0 auto 2.75rem;
  text-align: center;
}

.section-head.left {
  margin: 0 0 2.75rem;
  text-align: left;
}

.lead {
  margin: 0;
  font-size: 1.075rem;
  line-height: 1.75;
  color: var(--text-muted);
  max-width: 64ch;
}

.section-head .lead {
  margin-inline: auto;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.48rem 0.85rem;
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  color: var(--primary);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.eyebrow svg {
  flex-shrink: 0;
}

.hero {
  padding-top: clamp(3rem, 6vw, 5rem);
  position: relative;
}

.hero-grid {
  display: grid;
  gap: 3rem;
  align-items: center;
}

.hero-copy {
  max-width: 58ch;
}

.hero-actions {
  display: flex;
  gap: 0.9rem;
  flex-wrap: wrap;
  margin-top: 2rem;
}

.hero-trust {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.35rem;
  padding: 0;
  margin: 2.1rem 0 0;
}

.hero-trust li {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-muted);
}

.hero-trust svg {
  color: var(--primary);
  flex-shrink: 0;
}

.hero-media {
  position: relative;
  width: 100%;
  max-width: 540px;
  justify-self: end;
}

.hero-image-main {
  width: 100%;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  border-radius: 32px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
}

.hero-image-secondary {
  position: absolute;
  width: 40%;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  border-radius: 24px;
  border: 5px solid var(--bg);
  box-shadow: var(--shadow);
  bottom: -9%;
  left: -7%;
}

.floating-chip {
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.78rem 0.95rem;
  border-radius: 16px;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  box-shadow: var(--shadow);
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text);
  backdrop-filter: blur(12px);
  max-width: min(75%, 240px);
}

.floating-chip svg {
  color: var(--primary);
  flex-shrink: 0;
}

.chip-top {
  top: 1.2rem;
  right: 1.2rem;
}

.chip-bottom {
  bottom: 1.2rem;
  left: 1.2rem;
}

.btn {
  min-height: 52px;
  border-radius: 999px;
  padding: 0.9rem 1.55rem;
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, var(--btn-from), var(--btn-to));
  color: #ffffff;
  box-shadow: 0 14px 32px rgba(15, 118, 110, 0.28);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 38px rgba(15, 118, 110, 0.36);
}

.btn-secondary {
  background: var(--surface);
  color: var(--primary);
  border-color: var(--border-strong);
}

.btn-secondary:hover {
  transform: translateY(-2px);
  background: var(--surface-2);
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 24px;
  box-shadow: var(--shadow);
  backdrop-filter: blur(14px);
}

.services-grid {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 1fr;
}

.service-card {
  padding: 1.55rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  height: 100%;
}

.service-icon {
  width: 54px;
  height: 54px;
  border-radius: 17px;
  display: grid;
  place-items: center;
  color: var(--primary);
  background: linear-gradient(135deg, rgba(15, 118, 110, 0.16), rgba(124, 58, 237, 0.12));
  border: 1px solid var(--border);
}

.service-card p {
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.68;
}

.disclaimer {
  margin-top: 1.6rem;
  text-align: center;
  font-size: 0.925rem;
  color: var(--text-muted);
}

.tours-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
}

.tour-card {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tour-media {
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
}

.tour-media img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.tour-overlay {
  position: absolute;
  inset: auto 0 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: linear-gradient(to top, rgba(5, 20, 18, 0.74), transparent);
}

.tour-duration,
.tour-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border-radius: 999px;
  padding: 0.38rem 0.72rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #ffffff;
  background: rgba(5, 20, 18, 0.58);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(8px);
}

.tour-tag {
  background: rgba(180, 83, 9, 0.78);
}

.tour-body {
  padding: 1.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  flex: 1;
}

.tour-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.925rem;
  font-weight: 600;
  color: var(--text-muted);
}

.tour-body p {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.68;
}

.tour-btn {
  margin-top: auto;
  width: 100%;
}

.carousel {
  position: relative;
  overflow: hidden;
  max-width: 1000px;
  margin: 0 auto;
}

.carousel-track {
  display: flex;
  transition: transform 0.65s cubic-bezier(0.16, 1, 0.3, 1);
}

.carousel-slide {
  position: relative;
  min-width: 100%;
  aspect-ratio: 16 / 9;
  margin: 0;
}

.carousel-slide img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.carousel-slide figcaption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 1.35rem;
  color: #ffffff;
  font-weight: 600;
  background: linear-gradient(to top, rgba(5, 20, 18, 0.78), transparent);
}

.carousel-dots {
  position: absolute;
  left: 50%;
  bottom: 1.1rem;
  transform: translateX(-50%);
  display: flex;
  gap: 0.45rem;
}

.carousel-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.45);
  transition: all 0.25s ease;
  padding: 0;
}

.carousel-dot.active {
  width: 28px;
  background: var(--primary);
}

.carousel-progress {
  position: absolute;
  right: 1.25rem;
  bottom: 1.45rem;
  width: 46px;
  height: 3px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.28);
  overflow: hidden;
}

.carousel-progress span {
  display: block;
  height: 100%;
  background: var(--primary);
  animation: carouselProgress 6s linear forwards;
}

@keyframes carouselProgress {
  from {
    transform: scaleX(1);
    transform-origin: left;
  }
  to {
    transform: scaleX(0);
    transform-origin: left;
  }
}

.steps-grid {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 1fr;
}

.step-card {
  padding: 1.6rem;
}

.step-number {
  font-family: 'Sora', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--accent);
  letter-spacing: 0.12em;
}

.step-card h3 {
  margin: 0.7rem 0 0.65rem;
}

.step-card p {
  margin: 0;
  line-height: 1.7;
}

.quote-card {
  padding: clamp(1.75rem, 4vw, 2.75rem);
  max-width: 860px;
  margin: 2rem auto 0;
}

.quote-stars {
  display: flex;
  gap: 0.2rem;
  color: var(--accent);
  margin-bottom: 1.1rem;
}

.quote-card blockquote {
  margin: 0 0 1.6rem;
}

.quote-card p {
  font-size: clamp(1.1rem, 2.4vw, 1.3rem);
  line-height: 1.72;
  color: var(--text);
  margin: 0;
}

.quote-footer {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.quote-avatar {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  color: #ffffff;
  font-weight: 700;
  background: linear-gradient(135deg, #0f766e, #7c3aed);
}

.quote-footer cite {
  font-style: normal;
  font-weight: 700;
  display: block;
}

.quote-footer span {
  color: var(--text-muted);
  font-size: 0.925rem;
}

.faq-list {
  display: grid;
  gap: 1rem;
  margin-top: 2rem;
}

.faq-item {
  padding: 0;
  overflow: hidden;
}

.faq-item summary {
  list-style: none;
  cursor: pointer;
  padding: 1.35rem 1.5rem;
  font-weight: 700;
  color: var(--text);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.faq-item summary::-webkit-details-marker {
  display: none;
}

.faq-item summary::after {
  content: '+';
  font-size: 1.4rem;
  color: var(--primary);
  transition: transform 0.25s ease;
  flex-shrink: 0;
}

.faq-item[open] summary::after {
  transform: rotate(45deg);
}

.faq-item p {
  margin: 0;
  padding: 0 1.5rem 1.5rem;
  line-height: 1.72;
}

.newsletter-card {
  padding: clamp(2rem, 5vw, 3.5rem);
  text-align: center;
  background: linear-gradient(135deg, rgba(15, 118, 110, 0.16), rgba(124, 58, 237, 0.14));
  border-color: var(--border-strong);
}

.newsletter-card .h2 {
  margin-bottom: 0.6rem;
}

.newsletter-card > p {
  max-width: 56ch;
  margin: 0 auto 1.75rem;
}

.newsletter-form {
  display: grid;
  gap: 0.9rem;
  max-width: 560px;
  margin: 0 auto;
}

.newsletter-input {
  width: 100%;
  min-height: 54px;
  border-radius: 999px;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  color: var(--text);
  padding: 0 1.35rem;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
}

.newsletter-input::placeholder {
  color: var(--text-muted);
  opacity: 0.75;
}

.success {
  padding: 1rem 1.25rem;
  border-radius: 16px;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  color: var(--text);
  font-weight: 600;
  max-width: 560px;
  margin: 0 auto;
}

.footer {
  border-top: 1px solid var(--border);
  padding: 2.5rem 0 3rem;
}

.footer-inner {
  display: grid;
  gap: 1rem;
  justify-items: center;
  text-align: center;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-family: 'Sora', sans-serif;
  font-weight: 700;
  font-size: 1.1rem;
}

.footer-brand svg {
  color: var(--primary);
}

.footer nav {
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
  justify-content: center;
}

.footer a {
  color: var(--text-muted);
  text-decoration: none;
  font-weight: 600;
}

.footer a:hover {
  color: var(--primary);
}

.footer-small {
  font-size: 0.875rem;
  max-width: 72ch;
  margin: 0;
}

@media (min-width: 700px) {
  .services-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .tours-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 800px) {
  .steps-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 980px) {
  .hero-grid {
    grid-template-columns: 1.04fr 0.96fr;
    gap: 4rem;
  }
}

@media (min-width: 1050px) {
  .tours-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 979px) {
  .hero-media {
    max-width: 620px;
    margin-inline: auto;
  }

  .hero-image-secondary {
    display: none;
  }
}

@media (max-width: 640px) {
  /* Tighter gutters — cards span nearly the full device width */
  .container {
    width: calc(100% - 1.5rem);
  }

  /* Reduce vertical rhythm on mobile — sections feel more native */
  .section {
    padding-block: clamp(3rem, 10vw, 4.5rem);
  }

  .section-head {
    margin: 0 auto 2rem;
  }

  .section-head.left {
    margin: 0 0 2rem;
  }

  /* Section headings scale down nicely on smaller phones */
  .h2 {
    font-size: clamp(1.65rem, 6vw, 2.85rem);
  }

  .lead {
    font-size: 1rem;
    line-height: 1.7;
  }

  /* Hero adjustments */
  .sectiona {
    margin-top: 2.5rem;
  }

  .hero {
    padding-top: clamp(2rem, 4vw, 3rem);
  }

  .hero-grid {
    gap: 2rem;
  }

  .h1 {
    font-size: clamp(2.2rem, 9vw, 4.5rem);
    line-height: 1.05;
  }

  .hero-title {
    max-width: 18ch;
  }

  .hero-copy {
    max-width: none;
  }

  /* Buttons stack full-width for easy thumb taps */
  .hero-actions {
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }

  .hero-actions .btn {
    width: 100%;
    justify-content: center;
  }

  .hero-trust {
    gap: 0.55rem 1.15rem;
    margin: 1.5rem 0 0;
  }

  .hero-trust li {
    font-size: 0.9rem;
  }

  /* Hero image full-bleed with better proportion */
  .hero-media {
    width: 100vw;
    max-width: 100vw;
    margin-inline: calc(50% - 50vw);
    margin-top: 1rem;
  }

  .hero-image-main {
    border-radius: 0;
    border-inline: 0;
    aspect-ratio: 3 / 4;
  }

  /* Cards & content */
  .service-card {
    padding: 1.25rem;
    gap: 0.7rem;
  }

  .service-icon {
    width: 48px;
    height: 48px;
    border-radius: 15px;
  }

  .service-card h3 {
    font-size: 1.08rem;
  }

  .service-card p {
    font-size: 0.92rem;
  }

  .tour-media {
    aspect-ratio: 4 / 3;
  }

  .tour-body {
    padding: 1.1rem 1.15rem 1.2rem;
    gap: 0.65rem;
  }

  .tour-body h3 {
    font-size: 1.08rem;
  }

  .tour-body p {
    font-size: 0.9rem;
    line-height: 1.6;
  }

  .tour-overlay {
    padding: 0.85rem;
  }

  .tour-duration,
  .tour-tag {
    font-size: 0.75rem;
    padding: 0.32rem 0.65rem;
  }

  /* Carousel */
  .carousel-slide {
    aspect-ratio: 1 / 1;
  }

  .carousel-dots {
    bottom: 0.85rem;
    gap: 0.5rem;
  }

  .carousel-dot {
    width: 12px;
    height: 12px;
  }

  .carousel-dot.active {
    width: 32px;
  }

  .carousel-progress {
    display: none;
  }

  /* Approach / Steps */
  .steps-grid {
    gap: 1rem;
  }

  .step-card {
    padding: 1.35rem;
  }

  .step-card h3 {
    font-size: 1.08rem;
  }

  .step-card p {
    font-size: 0.92rem;
    line-height: 1.65;
  }

  /* Testimonial */
  .quote-card {
    padding: 1.5rem;
    margin: 1.5rem auto 0;
  }

  .quote-card p {
    font-size: 1.02rem;
    line-height: 1.65;
  }

  .quote-card blockquote {
    margin: 0 0 1.25rem;
  }

  .quote-footer {
    gap: 0.85rem;
  }

  .quote-avatar {
    width: 46px;
    height: 46px;
  }

  /* FAQ */
  .faq-list {
    gap: 0.75rem;
    margin-top: 1.5rem;
  }

  .faq-item summary {
    padding: 1.05rem 1.2rem;
    font-size: 0.95rem;
    gap: 0.75rem;
  }

  .faq-item summary::after {
    font-size: 1.3rem;
  }

  .faq-item p {
    padding: 0 1.2rem 1.2rem;
    font-size: 0.9rem;
    line-height: 1.65;
  }

  /* Newsletter */
  .newsletter-card {
    padding: 1.75rem 1.25rem;
  }

  .newsletter-form {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .newsletter-input {
    min-height: 50px;
    font-size: 0.95rem;
  }

  /* Buttons */
  .btn {
    min-height: 50px;
    font-size: 0.95rem;
    padding: 0.85rem 1.4rem;
  }

  /* Footer */
  .footer {
    padding: 2rem 0 2.5rem;
  }

  .footer-inner {
    gap: 0.85rem;
  }

  .footer nav {
    gap: 1.1rem;
  }

  .footer a {
    font-size: 0.9rem;
    padding: 0.25rem 0;
  }

  .footer-small {
    font-size: 0.8rem;
    line-height: 1.5;
  }

  .chip-bottom {
    display: none;
  }

  .floating-chip {
    font-size: 0.78rem;
    padding: 0.55rem 0.65rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .home-root *,
  .home-root *::before,
  .home-root *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
`;

const Home = ({ isDarkMode = false }) => {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryPaused, setGalleryPaused] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    const previousBackground = document.body.style.backgroundColor;
    const previousColor = document.body.style.color;
    const previousColorScheme = document.body.style.colorScheme;

    document.body.style.backgroundColor = isDarkMode ? '#061110' : '#F7FBFA';
    document.body.style.color = isDarkMode ? '#E9F6F3' : '#132E29';
    document.body.style.colorScheme = isDarkMode ? 'dark' : 'light';

    return () => {
      document.body.style.backgroundColor = previousBackground;
      document.body.style.color = previousColor;
      document.body.style.colorScheme = previousColorScheme;
    };
  }, [isDarkMode]);

  useEffect(() => {
    if (reduceMotion || galleryPaused) return undefined;

    const id = setInterval(() => {
      setGalleryIndex(prev => (prev + 1) % GALLERY_IMAGES.length);
    }, 6000);

    return () => clearInterval(id);
  }, [reduceMotion, galleryPaused]);

  const scrollToServices = () => {
    document.getElementById('services-heading')?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start'
    });
  };

  const reveal = (delay = 0) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 22 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: '0px 0px -10% 0px' },
          transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }
        };

  const heroReveal = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 26 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
      };

  const heroMediaReveal = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 1.03 },
        animate: { opacity: 1, scale: 1 },
        transition: { duration: 0.85, delay: 0.12, ease: [0.16, 1, 0.3, 1] }
      };

  return (
    <main
      className="home-root"
      style={{
        '--bg': isDarkMode ? '#061110' : '#F7FBFA',
        '--bg-muted': isDarkMode ? '#0A1917' : '#EFF7F5',
        '--surface': isDarkMode ? 'rgba(16, 38, 35, 0.72)' : '#FFFFFF',
        '--surface-2': isDarkMode ? 'rgba(10, 27, 25, 0.78)' : '#F4FAF9',
        '--border': isDarkMode ? 'rgba(94, 234, 212, 0.14)' : 'rgba(15, 118, 110, 0.14)',
        '--border-strong': isDarkMode ? 'rgba(94, 234, 212, 0.26)' : 'rgba(15, 118, 110, 0.26)',
        '--text': isDarkMode ? '#E9F6F3' : '#132E29',
        '--text-muted': isDarkMode ? '#A8C5BE' : '#4A645E',
        '--primary': isDarkMode ? '#2DD4BF' : '#0F766E',
        '--btn-from': isDarkMode ? '#0D9488' : '#0F766E',
        '--btn-to': isDarkMode ? '#0F766E' : '#134E4A',
        '--accent': isDarkMode ? '#F59E0B' : '#B45309',
        '--focus': isDarkMode ? 'rgba(94, 234, 212, 0.65)' : 'rgba(15, 118, 110, 0.45)',
        '--shadow': isDarkMode ? '0 18px 50px rgba(0, 0, 0, 0.42)' : '0 18px 50px rgba(15, 118, 110, 0.10)'
      }}
    >
      <style>{globalStyles}</style>

      {/* HERO */}
      <section className="section sectiona hero" aria-labelledby="hero-heading">
        <div className="container hero-grid">
          <motion.div {...heroReveal}>
            {/* <p className="eyebrow">
              <Plane size={14} aria-hidden="true" />
              Oscar Global · Est. 2025
            </p> */}

            <h1 id="hero-heading" className="h1 hero-title">
              Flawless Travel Tailored for You.
            </h1>

            <p className="lead hero-copy">
              We help individuals, families, teams and cooperate organizations plan trips with clarity: flights, visa guidance,
              curated itineraries, and other logistics support. 
            </p>

            <div className="hero-actions">
              <button className="btn btn-primary" onClick={() => navigate('/booking')}>
                Start your trip
                <ArrowRight size={18} aria-hidden="true" />
              </button>

              <button className="btn btn-secondary" onClick={scrollToServices}>
                View services
              </button>
            </div>

            <ul className="hero-trust" aria-label="Service highlights">
              <li>
                <Calendar size={16} aria-hidden="true" />
                End-to-end planning
              </li>
              <li>
                <IdCard size={16} aria-hidden="true" />
                Document review
              </li>
              <li>
                <ShieldCheck size={16} aria-hidden="true" />
                 Support
              </li>
            </ul>
          </motion.div>

          <motion.div className="hero-media" {...heroMediaReveal}>
            <img
              src={galleryImg01}
              alt="Overwater villa at sunset"
              className="hero-image-main"
              loading="eager"
            />

            <img
              src={galleryImg02}
              alt="London skyline"
              className="hero-image-secondary"
              loading="lazy"
            />

            {/* <div className="floating-chip chip-top">
              <CheckCircle size={17} aria-hidden="true" />
              Visa document checklist
            </div> */}

            {/* <div className="floating-chip chip-bottom">
              <Plane size={17} aria-hidden="true" />
              Flight change assistance
            </div> */}
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        className="section"
        aria-labelledby="services-heading"
        style={{ background: 'var(--bg-muted)' }}
      >
        <div className="container">
          <div className="section-head">
            <motion.h2 id="services-heading" className="h2" {...reveal()}>
              What We Do
            </motion.h2>
            <motion.p className="lead" {...reveal(0.05)}>
              Reliable travel solutions, delivered with expertise and responsive support.
            </motion.p>
          </div>

          <div className="services-grid">
            {SERVICES.map((service, index) => (
              <motion.article
                key={service.title}
                className="card service-card"
                {...reveal(index * 0.06)}
              >
                <div className="service-icon" aria-hidden="true">
                  {service.icon}
                </div>
                <h3 className="h3">{service.title}</h3>
                <p>{service.desc}</p>
              </motion.article>
            ))}
          </div>

          
        </div>
      </section>

      {/* FEATURED TRIPS */}
      <section className="section" aria-labelledby="trips-heading">
        <div className="container">
          <div className="section-head">
            <motion.h2 id="trips-heading" className="h2" {...reveal()}>
              Featured Journeys
            </motion.h2>
            <motion.p className="lead" {...reveal(0.05)}>
              Sample journeys we can help plan, price, and coordinate to suit your travel needs.
            </motion.p>
          </div>

          <div className="tours-grid">
            {FEATURED_TRIPS.map((trip, index) => (
              <motion.article
                key={trip.title}
                className="card tour-card"
                {...reveal(index * 0.08)}
              >
                <div className="tour-media">
                  <img src={trip.image} alt={trip.alt} loading="lazy" />
                  <div className="tour-overlay">
                    <span className="tour-duration">
                      {/* <Calendar size={14} aria-hidden="true" /> */}
                      {trip.days}
                    </span>
                    {/* <span className="tour-tag">Signature</span> */}
                  </div>
                </div>

                <div className="tour-body">
                  <h3 className="h3">{trip.title}</h3>

                  {/* <p className="tour-meta">
                    <MapPin size={15} aria-hidden="true" />
                    {trip.route}
                  </p> */}

                  <p>{trip.inclusions}</p>

                  <button
                    className="btn btn-secondary tour-btn"
                    onClick={() => navigate('/booking')}
                  >
                    Request quote
                    <ArrowRight size={16} aria-hidden="true" />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section
        className="section"
        aria-labelledby="gallery-heading"
        style={{ background: 'var(--bg-muted)' }}
      >
        <div className="container">
          <div className="section-head">
            <motion.h2 id="gallery-heading" className="h2" {...reveal()}>
              Destinations Worth Discovering
            </motion.h2>
            <motion.p className="lead" {...reveal(0.05)}>
              From romantic escapes and corporate experiences to unforgettable cruises.
            </motion.p>
          </div>

          <div
            className="carousel card"
            onMouseEnter={() => setGalleryPaused(true)}
            onMouseLeave={() => setGalleryPaused(false)}
            onFocusCapture={() => setGalleryPaused(true)}
            onBlurCapture={() => setGalleryPaused(false)}
            aria-roledescription="carousel"
            aria-label="Destination gallery"
          >
            <div
              className="carousel-track"
              style={{ transform: `translateX(-${galleryIndex * 100}%)` }}
            >
              {GALLERY_IMAGES.map((image, index) => (
                <figure
                  key={image.src}
                  className="carousel-slide"
                  aria-hidden={index !== galleryIndex ? true : undefined}
                  aria-label={`Slide ${index + 1} of ${GALLERY_IMAGES.length}: ${image.alt}`}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    loading={index === galleryIndex ? 'eager' : 'lazy'}
                  />
                  {/* <figcaption>{image.alt}</figcaption> */}
                </figure>
              ))}
            </div>

            <div className="carousel-dots" aria-label="Gallery navigation">
              {GALLERY_IMAGES.map((_, index) => (
                <button
                  key={GALLERY_IMAGES[index].src}
                  type="button"
                  className={`carousel-dot ${index === galleryIndex ? 'active' : ''}`}
                  onClick={() => setGalleryIndex(index)}
                  aria-label={`View slide ${index + 1}`}
                  aria-current={index === galleryIndex}
                />
              ))}
            </div>

            {!reduceMotion && (
              <span className="carousel-progress" aria-hidden="true">
                <span
                  key={galleryIndex}
                  style={{ animationPlayState: galleryPaused ? 'paused' : 'running' }}
                />
              </span>
            )}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="section" aria-labelledby="approach-heading">
        <div className="container">
          <div className="section-head left">
            <motion.h2 id="approach-heading" className="h2" {...reveal()}>
              Travel, Made Simpler and More Personal
            </motion.h2>
            <motion.p className="lead" {...reveal(0.05)}>
              Oscar Global was to make travel planning simpler, more thoughtful, and tailored to each traveller.
            </motion.p>
          </div>

          <div className="steps-grid">
            {APPROACH.map((step, index) => (
              <motion.article key={step.title} className="card step-card" {...reveal(index * 0.07)}>
                <span className="step-number">0{index + 1}</span>
                <h3 className="h3">{step.title}</h3>
                <p>{step.desc}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section
        className="section"
        aria-labelledby="feedback-heading"
        style={{ background: 'var(--bg-muted)' }}
      >
        <div className="container narrow">
          <motion.h2 id="feedback-heading" className="h2 center" {...reveal()}>
            Client feedback
          </motion.h2>

          <motion.blockquote className="card quote-card" {...reveal(0.08)}>
            <div className="quote-stars" aria-label="Rated 5 out of 5">
              {[...Array(5)].map((_, index) => (
                <Star key={index} size={18} fill="currentColor" strokeWidth={0} aria-hidden="true" />
              ))}
            </div>

            <blockquote>
              <p>
                “Oscar Global made our business trip easier to manage. They organised the flights, kept
                our visa documents in order, and responded quickly when a schedule changed. The
                communication was clear from start to finish.”
              </p>
            </blockquote>

            <div className="quote-footer">
              <div className="quote-avatar" aria-hidden="true">
                AC
              </div>
              <div>
                <cite>Adaobi C.</cite>
                <span>Operations Manager · Lagos</span>
              </div>
            </div>
          </motion.blockquote>
        </div>
      </section>

      {/* FAQ */}
      <section className="section" aria-labelledby="faq-heading">
        <div className="container narrow">
          <motion.h2 id="faq-heading" className="h2 center" {...reveal()}>
            Questions, answered plainly
          </motion.h2>

          <div className="faq-list">
            {FAQS.map((faq, index) => (
              <motion.details key={faq.q} className="card faq-item" {...reveal(index * 0.05)}>
                <summary>{faq.q}</summary>
                <p>{faq.a}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="section" aria-labelledby="newsletter-heading">
        <div className="container narrow">
          <motion.div className="card newsletter-card" {...reveal()}>
            <h2 id="newsletter-heading" className="h2">
              Travel updates, occasionally
            </h2>
            <p>Destination notes, visa reminders, and planning tips. </p>

            {subscribed ? (
              <div className="success" role="status">
                Thanks — you’re on the list.
              </div>
            ) : (
              <form
                className="newsletter-form"
                onSubmit={event => {
                  event.preventDefault();
                  const form = event.currentTarget;
                  form.reset();
                  setSubscribed(true);
                }}
              >
                <label htmlFor="newsletter-email" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                  Email address
                </label>
                <input
                  id="newsletter-email"
                  className="newsletter-input"
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  required
                />
                <button className="btn btn-primary" type="submit">
                  <Send size={18} aria-hidden="true" />
                  Subscribe
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="container footer-inner">
          <div className="footer-brand">
            <Plane size={18} aria-hidden="true" />
            Oscar Global
          </div>

          

          <nav aria-label="Footer navigation">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="/contact">Contact</a>
          </nav>

          <p className="footer-small">
            © {new Date().getFullYear()} Oscar Global. All rights reserved.
          </p>
        </div>
      </footer>
    </main>
  );
};

export default Home;













































































































































